/**
 * Pure helpers for the attack dice-count selector.
 *
 * The dice count is a controlled choice the player makes (1, 2, or 3). A bug
 * used to reset it to the max on every re-render — the reset effect keyed on the
 * `selectedFrom` territory OBJECT, whose identity churns on every WS state patch,
 * so a player who picked "2" would silently attack with "3". These helpers make
 * the rule explicit and unit-testable.
 */

import { DICE_COUNT_OUT_OF_RANGE } from '@warcarp/api-types'

/**
 * Max dice a source holding `armies` may roll: the classic Risk cap of 3, and
 * you must leave at least 1 army behind (so `armies - 1`). Floored at 1.
 */
export function maxDiceForArmies(armies: number): number {
  return Math.max(1, Math.min(3, armies - 1))
}

/** The default and minimum number of armies a continuous attack leaves behind. */
export const DEFAULT_BLITZ_FLOOR = 3

/**
 * A continuous attack always starts with the three-dice worst case in mind.
 * Keeping three armies behind means the selector remains safe when the source
 * is using the default three dice, rather than allowing a choice that the API
 * would reject on the first roll.
 */
export function maxBlitzFloorForArmies(armies: number): number {
  return Math.max(0, armies - 3)
}

/**
 * Choices for the continuous-attack stop point. An empty list means the source
 * is too small for a safe three-army floor (fewer than six armies).
 */
export function blitzFloorOptions(armies: number): number[] {
  const max = maxBlitzFloorForArmies(armies)
  if (max < DEFAULT_BLITZ_FLOOR) return []
  return Array.from(
    { length: max - DEFAULT_BLITZ_FLOOR + 1 },
    (_, index) => DEFAULT_BLITZ_FLOOR + index,
  )
}

/** Keep a selected floor valid as the source stack changes after a roll. */
export function clampBlitzFloor(floor: number, armies: number): number {
  const options = blitzFloorOptions(armies)
  if (options.length === 0) return DEFAULT_BLITZ_FLOOR
  return Math.max(DEFAULT_BLITZ_FLOOR, Math.min(floor, options[options.length - 1] ?? DEFAULT_BLITZ_FLOOR))
}

/**
 * The dice count to use for the current render, PRESERVING the player's explicit
 * pick:
 *   - a NEW source defaults to its max (the "dice defaults to max" behaviour);
 *   - the SAME source keeps the player's pick, only clamped DOWN if the source
 *     weakened below it (never bumped back up).
 *
 * Critically, when neither the source nor its max changed (an unrelated
 * re-render), `sourceChanged` is false and the pick is returned unchanged — no
 * more silent reset to max.
 */
export function resolveDiceCount(opts: {
  prev: number
  sourceChanged: boolean
  maxDice: number
}): number {
  const { prev, sourceChanged, maxDice } = opts
  if (sourceChanged) return maxDice
  return Math.min(prev, maxDice)
}

/**
 * Armies a source needs before continuous attack ("blitz") is on by DEFAULT.
 *
 * Below six, a three-army floor cannot safely accommodate the default three-dice
 * roll. Continuous attack is therefore unavailable until the source has six
 * armies, which guarantees at least one safe blitz roll.
 */
export const BLITZ_MIN_ARMIES = DEFAULT_BLITZ_FLOOR + 3

/**
 * The continuous-attack setting to use for the current render.
 *
 * The toggle is sticky by design — a player who blitzes usually wants to blitz
 * again — but it stayed on across a change of source, so picking a new front with
 * four armies on it and tapping Attack spent the lot. It now switches itself off
 * whenever the source CHANGES to (or weakens into) a stack under
 * `BLITZ_MIN_ARMIES`.
 *
 * Same shape and same reason as `resolveDiceCount`: only a source change may
 * override the player. With the source unchanged the value is returned untouched,
 * so deliberately switching blitz on for a small stack sticks rather than being
 * flipped back by the next re-render.
 */
export function resolveBlitz(opts: {
  prev: boolean
  sourceChanged: boolean
  armies: number | null
}): boolean {
  const { prev, sourceChanged, armies } = opts
  if (!prev) return false
  if (!sourceChanged || armies === null) return prev
  return armies >= BLITZ_MIN_ARMIES
}

/**
 * The dice count the server will accept now, when an attack was refused with
 * DICE_COUNT_OUT_OF_RANGE (the source thinned under the player's pick between
 * two rolls). `null` for every other failure.
 */
export function diceCountFromConflict(error: unknown): number | null {
  // Duck-typed on HttpError's `code` / `body` so this module stays free of
  // the fetch layer and its native imports.
  const failure = error as { code?: unknown; body?: { maxDice?: unknown } } | null
  if (!failure || typeof failure !== 'object' || failure.code !== DICE_COUNT_OUT_OF_RANGE) return null
  const max = failure.body?.maxDice
  return typeof max === 'number' && max >= 1 ? max : null
}

/** Panel copy for a failed attack; the dice conflict gets plain words, not the wire message. */
export function attackErrorMessage(error: unknown): string {
  const serverMax = diceCountFromConflict(error)
  if (serverMax !== null) return `Your stack thinned — down to ${serverMax} ${serverMax === 1 ? 'die' : 'dice'}. Tap again.`
  return error instanceof Error ? error.message : 'Attack failed'
}
